import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  useIonViewWillLeave,
} from '@ionic/react';
import { callOutline } from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { useUserContext } from '@/modules/user/useUserContext';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { useAppToast } from '@/hooks/useAppToast';
import PhoneInput from '@/components/PhoneInput';
import PhoneVerificationModal from '@/modules/idv/PhoneVerificationModal';
import { PhoneLogic } from '@/modules/phone';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { pxToRem } from '@/modules/config/constants';

/**
 * PhoneMigrationOldPhoneStep (#441): prove control of the existing account by
 * verifying the OLD number — a code texted to it. The caller enters their old
 * number and taps Verify; the verification modal identifies to the profile that
 * owns it and gives the session a live phone claim. On success we stash the old
 * number as originalPhone (so the migration can text it a change notice) and
 * advance to the new-phone step to finish switching.
 *
 * This is one of three proof methods chosen on PhoneMigrationProofStep; the
 * others (email, security questions) live on their own steps.
 */
const PhoneMigrationOldPhoneStep: React.FC = () => {
  const { setPendingPhone: setContextPendingPhone } = useUserContext();
  const {
    setOriginalPhone,
    urlProvidedOrigin,
    routerProvidedOrigin,
    navigateWithOrigin,
    navigateToStep,
    progress,
  } = useProfileWizardContext();
  const [presentToast] = useAppToast();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const verificationSucceededRef = useRef(false);
  const [oldPhone, setOldPhone] = useState('');
  const [showModal, setShowModal] = useState(false);

  const oldPhoneObj = PhoneLogic.tryCreate(oldPhone);
  const oldPhoneIsValid = oldPhoneObj !== null;

  // Stage the old number so the verification modal (which reads the context
  // phone and identifies to its owner) verifies THAT number.
  const handleVerifyOldPhone = () => {
    if (!oldPhoneObj) {
      presentToast({
        message: 'Enter a valid US phone number.',
        color: 'danger',
        duration: 3000,
      });
      return;
    }
    setContextPendingPhone(oldPhoneObj);
    setShowModal(true);
  };

  const getContinuePath = (): string => {
    const origin = routerProvidedOrigin || urlProvidedOrigin || '/';
    return `/profile/steps/phone-migration-new-phone?origin=${encodeURIComponent(origin)}`;
  };

  const handleModalSuccess = () => {
    verificationSucceededRef.current = true;
    setShowModal(false);
    // Record the verified old number so the migration can text it a change
    // notice when it completes, then advance to verify the new number.
    setOriginalPhone(oldPhone);
    navigateWithOrigin('/profile/steps/phone-migration-new-phone', 'forward');
  };

  const handleModalClose = (reason?: string) => {
    setShowModal(false);
    if (reason === 'success') {
      verificationSucceededRef.current = true;
      return;
    }
    if (verificationSucceededRef.current) {
      verificationSucceededRef.current = false;
      return;
    }
    if (reason === 'expired') {
      presentToast({
        message: 'Verification code expired. Please try verifying again.',
        color: 'warning',
        duration: 5000,
      });
    }
  };

  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('phone-migration-old-phone')}
        progress={progress}
        onBack={() =>
          navigateToStep(
            ProfileWizardLogic.previousStep('phone-migration-old-phone'),
            'back'
          )
        }
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          <IonText>
            <p style={{ fontSize: pxToRem(16), marginBottom: '10px' }}>
              Enter your old number and we'll text it a verification code to
              confirm it's you.
            </p>
          </IonText>
          <IonItem>
            <IonLabel position="stacked">
              <IonIcon icon={callOutline} style={{ marginRight: '5px' }} />
              Your old phone number
            </IonLabel>
            <PhoneInput
              value={oldPhone}
              onPhoneChange={setOldPhone}
              placeholder="(555) 123-4567"
              showValidation={true}
            />
          </IonItem>
        </div>
      </IonContent>

      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleVerifyOldPhone}
            disabled={!oldPhoneIsValid}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            Verify old number
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>

      <PhoneVerificationModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        continuePath={getContinuePath()}
      />
    </IonPage>
  );
};

export default PhoneMigrationOldPhoneStep;
